import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import PaidAdsEntry from './PaidAdsEntry';
import MonthlySummaryStep from '../components/MonthlySummaryStep';

var MONTHS_HE = ['ינואר','פברואר','מרץ','אפריל','מאי','יוני','יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר'];

var EMAIL_PRESETS = ['shahar@intermax.co.il', 'eli@intermax.co.il'];

function formatMonth(m) {
  if (!m) return '';
  var parts = m.split('-');
  var y = parts[0];
  var mon = parseInt(parts[1], 10);
  return MONTHS_HE[mon - 1] + ' ' + y;
}

// Hebrew month name only (no year), e.g. "יוני" — used in the email opening line.
function monthNameHe(m) {
  if (!m) return '';
  var mon = parseInt((m.split('-')[1]) || '', 10);
  return MONTHS_HE[mon - 1] || '';
}

// Numbered heading for the ordered workflow: month -> data -> summary ->
// settings -> generate -> send. Keeps the page readable as a sequence rather than a pile.
function stepHeader(n, title, subtitle) {
  return React.createElement('div', { style: { marginBottom: '12px' } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
      React.createElement('span', {
        style: {
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
          background: 'var(--accent)', color: '#fff', fontSize: '13px', fontWeight: 700,
        }
      }, String(n)),
      React.createElement('div', { style: { fontWeight: 700, fontSize: '16px' } }, title)
    ),
    subtitle && React.createElement('div', {
      style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', paddingRight: '34px' }
    }, subtitle)
  );
}

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Plain-text monthly summary -> HTML for the rich email editor: "* " lines
// become a bullet list, "1. " lines a numbered list, the rest paragraphs.
function summaryTextToHtml(text) {
  var html = '', open = null;
  function close() { if (open) { html += '</' + open + '>'; open = null; } }
  String(text || '').split('\n').forEach(function(raw) {
    var line = raw.trim();
    if (!line) { close(); return; }
    var bullet = /^[*•-]\s+(.*)$/.exec(line);
    var numbered = /^\d+[.)]\s+(.*)$/.exec(line);
    var kind = bullet ? 'ul' : (numbered ? 'ol' : null);
    if (!kind) { close(); html += '<p>' + escapeHtml(line) + '</p>'; return; }
    if (open !== kind) { close(); html += '<' + kind + '>'; open = kind; }
    html += '<li>' + escapeHtml((bullet || numbered)[1]) + '</li>';
  });
  close();
  return html;
}

// Fallback email body when no monthly summary was produced (step 3) — a bare
// opening line with the month derived from the report, never hand-typed.
function buildEmailTemplate(month, siteName) {
  var mn = monthNameHe(month);
  var forWhom = siteName ? (' עבור ' + siteName) : '';
  return '<p>שלום,</p>' +
    '<p>להלן סיכום הקידום האורגני לחודש ' + mn + forWhom + ':</p>';
}

// Earliest year selectable in the picker (back-dated reports for old clients).
var YEAR_FLOOR = 2020;

function getDefaultMonth() {
  var d = new Date();
  var m = d.getMonth(); // 0-indexed, so current month -1 for "previous month"
  var y = d.getFullYear();
  if (m === 0) { y--; m = 12; }
  return y + '-' + (m < 10 ? '0' + m : m);
}

function joinMonth(year, month) {
  return year + '-' + (month < 10 ? '0' + month : month);
}

function splitMonth(m) {
  var p = (m || '').split('-');
  return { year: parseInt(p[0], 10), month: parseInt(p[1], 10) };
}

function currentYM() {
  var d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

// Years from the current year down to the floor (newest first).
function getYearOptions() {
  var cur = new Date().getFullYear();
  var years = [];
  for (var y = cur; y >= YEAR_FLOOR; y--) years.push(y);
  return years;
}

// Month options for a given year, never past the current month.
function monthOptionsForYear(year) {
  var cur = currentYM();
  var maxMonth = (year === cur.year) ? cur.month : 12;
  var opts = [];
  for (var m = 1; m <= maxMonth; m++) {
    opts.push({ value: joinMonth(year, m), label: MONTHS_HE[m - 1] });
  }
  return opts;
}

// Clamp a YYYY-MM so it never lands in the future.
function clampMonth(m) {
  var cur = currentYM();
  var s = splitMonth(m);
  if (s.year > cur.year || (s.year === cur.year && s.month > cur.month)) {
    return joinMonth(cur.year, cur.month);
  }
  return m;
}

// Inclusive list of YYYY-MM strings from `fromM` to `toM` (empty if reversed).
function monthsInRange(fromM, toM) {
  var a = splitMonth(fromM), b = splitMonth(toM);
  var out = [];
  var y = a.year, mo = a.month, guard = 0;
  while ((y < b.year || (y === b.year && mo <= b.month)) && guard < 240) {
    out.push(joinMonth(y, mo));
    mo++; if (mo > 12) { mo = 1; y++; }
    guard++;
  }
  return out;
}

// A year+month pair of <select>s bound to a YYYY-MM value.
function YearMonthPicker(props) {
  var s = splitMonth(props.value);
  var selectStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '14px' };
  return React.createElement('div', { style: { display: 'flex', gap: '8px' } },
    React.createElement('select', {
      value: s.year,
      onChange: function(e) { props.onChange(clampMonth(joinMonth(parseInt(e.target.value, 10), s.month))); },
      className: 'form-select', style: selectStyle,
    }, getYearOptions().map(function(y) { return React.createElement('option', { key: y, value: y }, y); })),
    React.createElement('select', {
      value: props.value,
      onChange: function(e) { props.onChange(e.target.value); },
      className: 'form-select', style: selectStyle,
    }, monthOptionsForYear(s.year).map(function(o) { return React.createElement('option', { key: o.value, value: o.value }, o.label); }))
  );
}

function StatusBadge({ status }) {
  var colors = {
    ready: { bg: 'rgba(34,197,94,0.12)', color: 'var(--green)', text: 'מוכן' },
    sent: { bg: 'rgba(37,99,235,0.12)', color: 'var(--accent)', text: 'נשלח' },
    generating: { bg: 'rgba(245,158,11,0.12)', color: 'var(--orange)', text: 'בהכנה...' },
    pending: { bg: 'rgba(100,116,139,0.12)', color: 'var(--text-muted)', text: 'ממתין' },
    failed: { bg: 'rgba(239,68,68,0.12)', color: 'var(--red)', text: 'שגיאה' },
  };
  var c = colors[status] || colors.pending;
  return React.createElement('span', {
    style: {
      display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
      fontSize: '12px', fontWeight: 600, background: c.bg, color: c.color,
    }
  }, c.text);
}

export default function SiteReports() {
  var params = useParams();
  var id = params.id;
  var [site, setSite] = useState(null);
  var [reports, setReports] = useState([]);
  var [selectedMonth, setSelectedMonth] = useState(getDefaultMonth());
  var [generating, setGenerating] = useState(false);
  var [message, setMessage] = useState(null);
  var [sendEmail, setSendEmail] = useState('');
  var [emailBody, setEmailBody] = useState('');
  var [sending, setSending] = useState(false);
  var [showSendDialog, setShowSendDialog] = useState(null);
  var [showCompetitors, setShowCompetitors] = useState(false);
  var [showGeo, setShowGeo] = useState(false);
  var [useGscPositions, setUseGscPositions] = useState(false);
  // null until loaded; the GEO section needs at least one finished measurement run
  var [geoRuns, setGeoRuns] = useState(null);
  useEffect(function() {
    setGeoRuns(null);
    api.getGeoProject(id).then(function(p) { setGeoRuns(p.done_runs || 0); }).catch(function() {});
  }, [id]);
  var [monthHasData, setMonthHasData] = useState(false);
  var [editorLoaded, setEditorLoaded] = useState(false);
  var [portal, setPortal] = useState(null);
  var [portalCopied, setPortalCopied] = useState(false);
  var [bulkMode, setBulkMode] = useState(false);
  var [bulkFrom, setBulkFrom] = useState(getDefaultMonth());
  var [bulkTo, setBulkTo] = useState(getDefaultMonth());
  var [bulkRunning, setBulkRunning] = useState(false);
  var [bulkStatus, setBulkStatus] = useState('');
  var [signatures, setSignatures] = useState([]);
  var [signatureId, setSignatureId] = useState(null);
  var [copiedReportId, setCopiedReportId] = useState(null);
  var [dataVersion, setDataVersion] = useState(0);
  var emailEditorRef = useRef(null);

  // Report for the selected month (may be undefined until generated)
  var currentReport = reports.find(function(r) { return r.month === selectedMonth; });
  var currentReportId = currentReport ? currentReport.id : null;

  var loadSite = useCallback(function() {
    api.getSite(id).then(setSite).catch(function() {});
  }, [id]);

  var loadReports = useCallback(function() {
    api.getReports(id).then(setReports).catch(function() {});
  }, [id]);

  useEffect(function() { loadSite(); loadReports(); }, [loadSite, loadReports]);

  // Load the global email signatures once; preselect the default (if any).
  useEffect(function() {
    api.getSignatures().then(function(list) {
      list = list || [];
      setSignatures(list);
      var def = list.find(function(s) { return s.is_default; });
      if (def) setSignatureId(def.id);
    }).catch(function() {});
  }, []);

  // Reset transient status when switching between sites — the component is not
  // remounted on a client-side site switch (same route), so stale "מייצר/שולח..."
  // banners and open dialogs would otherwise carry over to the next client.
  useEffect(function() {
    setMessage(null);
    setGenerating(false);
    setSending(false);
    setBulkRunning(false);
    setBulkStatus('');
    setShowSendDialog(null);
    setPortal(null);
    setPortalCopied(false);
    setCopiedReportId(null);
  }, [id]);

  // Re-gate on every month/site switch; PaidAdsEntry reports the real value
  // back as soon as it has loaded that month.
  useEffect(function() { setMonthHasData(false); }, [id, selectedMonth]);

  // Keep the "show competitors" checkbox in sync with the selected month's report
  useEffect(function() {
    setShowCompetitors(currentReport ? !!currentReport.show_competitors : false);
    setShowGeo(currentReport ? !!currentReport.show_geo : false);
    setUseGscPositions(currentReport ? !!currentReport.use_gsc_positions : false);
  }, [currentReportId, selectedMonth]);

  function handleToggleCompetitors(e) {
    var val = e.target.checked;
    setShowCompetitors(val);
    api.saveReportConfig(id, selectedMonth, { show_competitors: val })
      .then(function() { loadReports(); })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה בשמירת הגדרה: ' + err.message }); });
  }

  function handleToggleGeo(e) {
    var val = e.target.checked;
    setShowGeo(val);
    api.saveReportConfig(id, selectedMonth, { show_geo: val })
      .then(function() { loadReports(); })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה בשמירת הגדרה: ' + err.message }); });
  }

  function handleToggleGscPositions(e) {
    var val = e.target.checked;
    setUseGscPositions(val);
    api.saveReportConfig(id, selectedMonth, { use_gsc_positions: val })
      .then(function() { loadReports(); })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה בשמירת הגדרה: ' + err.message }); });
  }

  function handleGenerate() {
    setGenerating(true);
    setMessage(null);
    api.generateReport(id, selectedMonth)
      .then(function(data) {
        setMessage({ type: 'success', text: 'הדוח נוצר בהצלחה!' });
        loadReports();
      })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה: ' + err.message }); })
      .finally(function() { setGenerating(false); });
  }

  // Poll a single month's report until it finishes generating.
  // Resolves true on success, false on failure/timeout.
  function pollReport(month) {
    return new Promise(function(resolve) {
      var attempts = 0;
      function check() {
        attempts++;
        api.getReport(id, month)
          .then(function(r) {
            if (r && (r.status === 'ready' || r.status === 'sent')) { resolve(true); return; }
            if (r && r.status === 'failed') { resolve(false); return; }
            if (attempts >= 60) { resolve(false); return; }
            setTimeout(check, 2000);
          })
          .catch(function() {
            // Report row may not exist yet (404) — keep polling until it appears.
            if (attempts >= 60) { resolve(false); return; }
            setTimeout(check, 2000);
          });
      }
      check();
    });
  }

  // Generate a report for every month in the selected range, one at a time,
  // so we don't hammer the GSC/GA4/DataForSEO APIs.
  function handleBulkGenerate() {
    var months = monthsInRange(bulkFrom, bulkTo);
    if (months.length === 0) {
      setMessage({ type: 'error', text: 'טווח לא תקין: חודש ההתחלה חייב להיות לפני או שווה לחודש הסיום.' });
      return;
    }
    if (months.length > 24) {
      setMessage({ type: 'error', text: 'טווח גדול מדי (מקסימום 24 חודשים).' });
      return;
    }
    setBulkRunning(true);
    setMessage(null);
    var idx = 0, done = 0, failed = 0;
    function next() {
      if (idx >= months.length) {
        setBulkRunning(false);
        setBulkStatus('');
        setMessage({
          type: failed ? 'error' : 'success',
          text: 'הסתיים: ' + done + ' דוחות נוצרו' + (failed ? ', ' + failed + ' נכשלו' : ''),
        });
        loadReports();
        return;
      }
      var m = months[idx];
      setBulkStatus((idx + 1) + '/' + months.length + ' — ' + formatMonth(m) + '...');
      api.generateReportAsync(id, m)
        .then(function() { return pollReport(m); })
        .then(function(ok) { if (ok) { done++; } else { failed++; } })
        .catch(function() { failed++; })
        .finally(function() { idx++; loadReports(); next(); });
    }
    next();
  }

  function handleDownload(reportId) {
    window.open((process.env.PUBLIC_URL || '') + '/api/reports/' + reportId + '/download', '_blank');
  }

  function handleView(report) {
    // Open the real web report (true rendering) in a new tab
    if (report && report.public_url) {
      window.open(window.location.origin + report.public_url, '_blank');
    }
  }

  function handleSend(reportId) {
    if (!sendEmail.trim()) return;
    setSending(true);
    var html = emailEditorRef.current ? emailEditorRef.current.getContent() : emailBody;
    api.sendReport(reportId, sendEmail, null, html, signatureId)
      .then(function() {
        setMessage({ type: 'success', text: 'הדוח נשלח בהצלחה ל-' + sendEmail });
        setShowSendDialog(null);
        setSendEmail('');
        setEmailBody('');
        loadReports();
      })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה בשליחה: ' + err.message }); })
      .finally(function() { setSending(false); });
  }

  // Open the send dialog with the body pre-filled from the saved monthly summary
  // of that report's month (step 3); falls back to the bare opening line.
  // Recipient and signature default to those of this site's previous report email,
  // else the site's client email and the default signature.
  function openSendDialog(reportId, month) {
    var def = signatures.find(function(s) { return s.is_default; });
    var bodyP = api.getMonthlySummary(id, month)
      .then(function(d) {
        var text = d && d.saved && d.saved.client_report;
        setEmailBody(text ? summaryTextToHtml(text) : buildEmailTemplate(month, site && site.name));
      })
      .catch(function() { setEmailBody(buildEmailTemplate(month, site && site.name)); });
    var lastP = api.getReportSendDefaults(id).catch(function() { return null; })
      .then(function(last) {
        setSendEmail((last && last.email) || (site && site.client_email) || '');
        setSignatureId(last && last.signature_known ? last.signature_id : (def ? def.id : null));
      });
    Promise.all([bodyP, lastP]).finally(function() { setShowSendDialog(reportId); });
  }

  // PaidAdsEntry reports after every load/save of the month's numbers; bump a
  // counter so the summary step re-reads them.
  function handleDataStatus(has) {
    setMonthHasData(has);
    setDataVersion(function(v) { return v + 1; });
  }

  function handleDelete(reportId) {
    if (!window.confirm('למחוק את הדוח?')) return;
    api.deleteReport(reportId)
      .then(function() {
        setMessage({ type: 'success', text: 'הדוח נמחק' });
        loadReports();
      })
      .catch(function(err) { setMessage({ type: 'error', text: err.message }); });
  }

  // ── TinyMCE CDN loader (shared; the email-body editor below depends on it) ──
  useEffect(function() {
    if (!editorLoaded && !document.getElementById('tinymce-script')) {
      var s = document.createElement('script');
      s.id = 'tinymce-script';
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/tinymce/6.8.2/tinymce.min.js';
      s.onload = function() { setEditorLoaded(true); };
      document.head.appendChild(s);
    } else if (window.tinymce) {
      setEditorLoaded(true);
    }
  }, [editorLoaded]);

  // ── Email body editor (rich text: bold/underline/lists) — only while dialog open ──
  useEffect(function() {
    if (showSendDialog && editorLoaded && window.tinymce) {
      window.tinymce.remove('#email-body-editor');
      window.tinymce.init({
        selector: '#email-body-editor', directionality: 'rtl', height: 240,
        menubar: false, plugins: 'lists link',
        toolbar: 'undo redo | bold italic underline | bullist numlist | link',
        content_style: "body{font-family:'Heebo',Arial,sans-serif;font-size:14px;line-height:1.8;direction:rtl;color:#e2e8f0;background:#111827;padding:12px}",
        skin: 'oxide-dark', content_css: 'dark',
        setup: function(ed) { emailEditorRef.current = ed; ed.on('init', function() { ed.setContent(emailBody || ''); }); },
      });
    }
    return function() { if (window.tinymce) window.tinymce.remove('#email-body-editor'); };
  }, [showSendDialog, editorLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadPortalLink() {
    api.getPortalLink(id)
      .then(function(d) { setPortal(d); })
      .catch(function(err) { setMessage({ type: 'error', text: 'שגיאה בטעינת קישור לקוח: ' + err.message }); });
  }

  function copyPortal() {
    if (!portal) return;
    var url = window.location.origin + portal.portal_url;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function() {
        setPortalCopied(true);
        setTimeout(function() { setPortalCopied(false); }, 2000);
      });
    }
  }

  // Copy the direct client-facing link for a single report (per history row).
  function copyReportLink(r) {
    if (!r || !r.public_url || !navigator.clipboard) return;
    var url = window.location.origin + r.public_url;
    navigator.clipboard.writeText(url).then(function() {
      setCopiedReportId(r.id);
      setTimeout(function() { setCopiedReportId(null); }, 2000);
    });
  }

  // Number of saved report versions for the selected month (full history).
  var versionsThisMonth = reports.filter(function(r) { return r.month === selectedMonth; }).length;
  // Sending needs a rendered PDF on the selected month's report.
  var canSend = !!(currentReport && currentReport.has_pdf);

  return React.createElement('div', { className: 'page-container' },
    // Header
    React.createElement('div', { className: 'page-header', style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' } },
      React.createElement('div', null,
        React.createElement('div', { className: 'page-title' }, 'דוחות חודשיים'),
        React.createElement('div', { className: 'page-subtitle' }, site ? site.name : '')
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' } },
        portal
          ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
              React.createElement('input', {
                readOnly: true,
                value: window.location.origin + portal.portal_url,
                onFocus: function(e) { e.target.select(); },
                style: { padding: '7px 10px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-muted)', fontSize: '12px', direction: 'ltr', width: '260px' }
              }),
              React.createElement('button', { className: 'btn btn-primary', onClick: copyPortal, style: { padding: '7px 14px', fontSize: '12px' } }, portalCopied ? '✓ הועתק' : 'העתק'),
              React.createElement('a', { className: 'btn btn-secondary', href: portal.portal_url, target: '_blank', rel: 'noreferrer', style: { padding: '7px 14px', fontSize: '12px' } }, 'פתח')
            )
          : React.createElement('button', { className: 'btn btn-secondary', onClick: loadPortalLink, style: { padding: '8px 16px' } }, '🔗 קישור לקוח')
      )
    ),

    // Message
    message && React.createElement('div', {
      style: {
        padding: '12px 16px', borderRadius: '8px', marginBottom: '16px',
        background: message.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
        color: message.type === 'success' ? 'var(--green)' : 'var(--red)',
        border: '1px solid ' + (message.type === 'success' ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'),
      }
    }, message.text),

    // ── Step 1: pick the month. Range mode is a self-contained back-fill path
    // (no data gate), so it keeps its own generate button right here.
    React.createElement('div', {
      className: 'section-card',
      style: { padding: '20px' }
    },
      stepHeader(1, 'בחר חודש'),
      React.createElement('div', null,
        // Mode toggle: single month vs. range
        React.createElement('div', { style: { display: 'flex', gap: '8px', marginBottom: '14px' } },
          React.createElement('button', {
            className: bulkMode ? 'btn btn-secondary' : 'btn btn-primary',
            onClick: function() { setBulkMode(false); },
            style: { padding: '6px 14px', fontSize: '13px' }
          }, 'חודש בודד'),
          React.createElement('button', {
            className: bulkMode ? 'btn btn-primary' : 'btn btn-secondary',
            onClick: function() { setBulkMode(true); },
            style: { padding: '6px 14px', fontSize: '13px' }
          }, 'טווח חודשים')
        ),
        // Single-month mode — picker only; creating the report is step 5.
        !bulkMode && React.createElement('div', null,
          React.createElement('label', { style: { fontSize: '13px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' } }, 'חודש הדוח'),
          React.createElement(YearMonthPicker, { value: selectedMonth, onChange: setSelectedMonth })
        ),
        // Range (bulk) mode
        bulkMode && React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' } },
            React.createElement('div', null,
              React.createElement('label', { style: { fontSize: '13px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' } }, 'מחודש'),
              React.createElement(YearMonthPicker, { value: bulkFrom, onChange: setBulkFrom })
            ),
            React.createElement('div', null,
              React.createElement('label', { style: { fontSize: '13px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' } }, 'עד חודש'),
              React.createElement(YearMonthPicker, { value: bulkTo, onChange: setBulkTo })
            ),
            React.createElement('button', {
              className: 'btn btn-primary',
              onClick: handleBulkGenerate,
              disabled: bulkRunning,
              style: { padding: '8px 16px' }
            }, bulkRunning ? 'מייצר דוחות...' : 'צור דוחות לטווח')
          ),
          bulkStatus && React.createElement('div', { style: { marginTop: '10px', fontSize: '13px', color: 'var(--text-secondary)' } }, bulkStatus),
          React.createElement('div', { style: { marginTop: '8px', fontSize: '12px', color: 'var(--text-muted)' } }, 'ייווצר דוח לכל חודש בטווח (מקסימום 24 חודשים). ריצה רציפה — עשוי לקחת מספר דקות. מצב זה אינו מחייב הזנת נתונים חודשיים מראש.')
        )
      )
    ),

    // ── Step 2: the monthly numbers. Reports can't be created without them.
    !bulkMode && React.createElement(PaidAdsEntry, {
      siteId: id, month: selectedMonth, step: 2, onStatusChange: handleDataStatus
    }),

    // ── Step 3: client email text (results vs. previous month + next month's plan)
    !bulkMode && React.createElement(MonthlySummaryStep, {
      siteId: id, month: selectedMonth, step: 3, refreshKey: dataVersion, locked: !monthHasData
    }),

    // ── Step 4: per-report settings
    !bulkMode && React.createElement('div', { className: 'section-card', style: { padding: '20px', marginTop: '16px' } },
      stepHeader(4, 'הגדרות דוח'),
      React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } },
        React.createElement('input', { type: 'checkbox', checked: showCompetitors, onChange: handleToggleCompetitors }),
        React.createElement('span', null, 'הצג מתחרים בדוח')
      ),
      React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' } },
        'בעמודת המיקומים יוצג הדומיין המדורג ראשון, עבור ביטויים שאיננו במקום 1. לאחר שינוי — יש ליצור מחדש את הדוח (צור דוח PDF).'),
      React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '14px' } },
        React.createElement('input', { type: 'checkbox', checked: showGeo && geoRuns !== 0, disabled: geoRuns === 0, onChange: handleToggleGeo }),
        React.createElement('span', { style: geoRuns === 0 ? { color: 'var(--text-muted)' } : null }, 'הצג נתוני GEO בדוח')
      ),
      geoRuns === 0
        ? React.createElement('div', { style: { fontSize: '12px', color: 'var(--red)', marginTop: '6px' } },
            'לאתר הזה אין עדיין ריצות של מדידת GEO, ולכן אין מה להציג בדוח. ',
            React.createElement(Link, { to: '/sites/' + id + '/geo-measurement', style: { color: 'var(--accent)' } }, 'להגדרת פרומפטים ולהרצת מדידה'))
        : React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' } },
            'סעיף "נראות במנועי AI": נקודת ההתחלה של מדידת ה-GEO מול הריצות האחרונות. לאחר שינוי — יש ליצור מחדש את הדוח.'),
      // TEMPORARY: while DataForSEO returns bot-degraded SERPs
      React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '14px' } },
        React.createElement('input', { type: 'checkbox', checked: useGscPositions && !!(site && site.gsc_property), disabled: !(site && site.gsc_property), onChange: handleToggleGscPositions }),
        React.createElement('span', { style: !(site && site.gsc_property) ? { color: 'var(--text-muted)' } : null }, 'מיקומים מ-Search Console')
      ),
      !(site && site.gsc_property)
        ? React.createElement('div', { style: { fontSize: '12px', color: 'var(--red)', marginTop: '6px' } },
            'לאתר הזה לא מחובר נכס Search Console.')
        : React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' } },
            'בטבלת הביטויים יוצג המיקום הממוצע בחודש לפי Google Search Console (חיפושים במדינת החיפוש של האתר) במקום מערכת המיקומים. ביטוי בלי חשיפות בחודש יוצג לפי מערכת המיקומים. לאחר שינוי — יש ליצור מחדש את הדוח.')
    ),

    // ── Step 5: create the report — gated on step 2 having data
    !bulkMode && React.createElement('div', { className: 'section-card', style: { padding: '20px', marginTop: '16px' } },
      stepHeader(5, 'יצירת דוח'),
      React.createElement('button', {
        className: 'btn btn-primary',
        onClick: handleGenerate,
        disabled: generating || !monthHasData,
        title: monthHasData ? '' : 'יש להזין ולשמור נתונים חודשיים (שלב 2) תחילה',
        style: {
          padding: '8px 16px',
          opacity: (generating || !monthHasData) ? 0.5 : 1,
          cursor: (generating || !monthHasData) ? 'not-allowed' : 'pointer',
        }
      }, generating ? 'מייצר דוח...' : 'צור דוח PDF'),
      !monthHasData && React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' } },
        'יש להזין ולשמור נתונים חודשיים (שלב 2) לפני יצירת הדוח.'),
      // Status of the selected month's report
      currentReport && React.createElement('div', {
        style: {
          marginTop: '16px', padding: '12px 16px', borderRadius: '8px',
          border: '1px solid var(--border)', background: 'var(--bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', flexWrap: 'wrap',
        }
      },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px' } },
          React.createElement(StatusBadge, { status: currentReport.status }),
          React.createElement('span', { style: { fontSize: '13px', color: 'var(--text-secondary)' } },
            'דוח ' + formatMonth(currentReport.month) + (versionsThisMonth > 1 ? ' (' + versionsThisMonth + ' גרסאות)' : '')
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
          currentReport.public_url && React.createElement('button', {
            className: 'btn btn-primary',
            onClick: function() { handleView(currentReport); },
            style: { padding: '6px 12px', fontSize: '12px' }
          }, 'צפה בדוח'),
          currentReport.public_url && React.createElement('button', {
            className: 'btn btn-secondary',
            onClick: function() { copyReportLink(currentReport); },
            style: { padding: '6px 12px', fontSize: '12px' }
          }, copiedReportId === currentReport.id ? '✓ הועתק' : 'העתק קישור לקוח'),
          currentReport.has_pdf && React.createElement('button', {
            className: 'btn btn-secondary',
            onClick: function() { handleDownload(currentReport.id); },
            style: { padding: '6px 12px', fontSize: '12px' }
          }, 'הורד PDF')
        )
      )
    ),

    // ── Step 6: send to the client — only once a PDF exists
    !bulkMode && React.createElement('div', { className: 'section-card', style: { padding: '20px', marginTop: '16px' } },
      stepHeader(6, 'שליחה ללקוח'),
      React.createElement('button', {
        className: 'btn btn-primary',
        onClick: function() { openSendDialog(currentReport.id, selectedMonth); },
        disabled: !canSend,
        title: canSend ? '' : 'יש ליצור דוח (שלב 5) תחילה',
        style: {
          padding: '8px 16px',
          opacity: canSend ? 1 : 0.5,
          cursor: canSend ? 'pointer' : 'not-allowed',
        }
      }, 'שלח במייל'),
      !canSend && React.createElement('div', { style: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '8px' } },
        'יש ליצור דוח (שלב 5) לפני השליחה.'),
      canSend && React.createElement('div', { style: { fontSize: '12px', marginTop: '8px', color: currentReport.sent_to_email ? 'var(--green)' : 'var(--text-muted)' } },
        currentReport.sent_to_email ? '✓ נשלח ל: ' + currentReport.sent_to_email : 'טרם נשלח')
    ),

    // Send dialog
    showSendDialog && React.createElement('div', {
      className: 'section-card',
      style: { padding: '20px', marginTop: '16px', border: '2px solid var(--accent)' }
    },
      React.createElement('div', { style: { fontWeight: 700, marginBottom: '12px' } }, 'שליחת דוח במייל'),
      // Quick-pick recipients
      React.createElement('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' } },
        EMAIL_PRESETS.map(function(addr) {
          var active = sendEmail === addr;
          return React.createElement('button', {
            key: addr,
            onClick: function() { setSendEmail(addr); },
            style: {
              padding: '5px 12px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', direction: 'ltr',
              border: '1px solid ' + (active ? 'var(--accent)' : 'var(--border)'),
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? '#fff' : 'var(--text-secondary)', fontFamily: 'inherit',
            }
          }, addr);
        })
      ),
      React.createElement('input', {
        type: 'email',
        value: sendEmail,
        onChange: function(e) { setSendEmail(e.target.value); },
        placeholder: 'email@example.com',
        style: {
          width: '100%', boxSizing: 'border-box', padding: '8px 12px', borderRadius: '8px',
          border: '1px solid var(--border)', background: 'var(--surface)',
          color: 'var(--text)', fontSize: '14px', direction: 'ltr', marginBottom: '10px',
        }
      }),
      React.createElement('label', {
        style: { fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }
      }, 'תוכן המייל (אופציונלי — אם ריק, יישלח טקסט ברירת המחדל)'),
      React.createElement('textarea', { id: 'email-body-editor', defaultValue: '' }),
      // Signature picker (global reusable signatures). "ללא חתימה" = none.
      signatures.length > 0 && React.createElement('div', { style: { marginTop: '10px', marginBottom: '10px' } },
        React.createElement('label', {
          style: { fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }
        }, 'חתימה'),
        React.createElement('select', {
          value: signatureId == null ? '' : String(signatureId),
          onChange: function(e) { setSignatureId(e.target.value ? parseInt(e.target.value, 10) : null); },
          style: {
            padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border)',
            background: 'var(--surface)', color: 'var(--text)', fontSize: '14px', minWidth: '240px',
          }
        },
          React.createElement('option', { value: '' }, 'ללא חתימה'),
          signatures.map(function(s) {
            return React.createElement('option', { key: s.id, value: String(s.id) }, s.name + (s.is_default ? ' (ברירת מחדל)' : ''));
          })
        )
      ),
      React.createElement('div', { style: { display: 'flex', gap: '10px', alignItems: 'center' } },
        React.createElement('button', {
          className: 'btn btn-primary',
          onClick: function() { handleSend(showSendDialog); },
          disabled: sending || !sendEmail.trim(),
          style: { padding: '8px 16px' }
        }, sending ? 'שולח...' : 'שלח'),
        React.createElement('button', {
          className: 'btn btn-secondary',
          onClick: function() { setShowSendDialog(null); },
          style: { padding: '8px 16px' }
        }, 'ביטול')
      )
    ),

    // Reports history table
    React.createElement('div', {
      className: 'section-card',
      style: { marginTop: '16px' }
    },
      React.createElement('div', {
        className: 'section-header',
        style: { padding: '16px 20px' }
      },
        React.createElement('div', { className: 'section-title' }, 'היסטוריית דוחות')
      ),
      reports.length === 0
        ? React.createElement('div', {
            style: { padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }
          }, 'אין דוחות עדיין')
        : React.createElement('table', {
            style: { width: '100%', borderCollapse: 'collapse' }
          },
            React.createElement('thead', null,
              React.createElement('tr', {
                style: { borderBottom: '1px solid var(--border)' }
              },
                ['חודש', 'סטטוס', 'נוצר', 'נשלח ל', 'פעולות'].map(function(h) {
                  return React.createElement('th', {
                    key: h,
                    style: {
                      padding: '10px 16px', textAlign: 'right', fontSize: '12px',
                      color: 'var(--text-muted)', fontWeight: 600,
                    }
                  }, h);
                })
              )
            ),
            React.createElement('tbody', null,
              reports.map(function(r) {
                return React.createElement('tr', {
                  key: r.id,
                  style: { borderBottom: '1px solid var(--border)' }
                },
                  React.createElement('td', { style: { padding: '10px 16px', fontSize: '14px', fontWeight: 600 } }, formatMonth(r.month)),
                  React.createElement('td', { style: { padding: '10px 16px' } }, React.createElement(StatusBadge, { status: r.status })),
                  React.createElement('td', { style: { padding: '10px 16px', fontSize: '12px', color: 'var(--text-muted)' } },
                    r.created_at ? new Date(r.created_at).toLocaleString('he-IL') : '-'
                  ),
                  React.createElement('td', { style: { padding: '10px 16px', fontSize: '12px', direction: 'ltr', textAlign: 'right' } },
                    r.sent_to_email
                      ? React.createElement('span', { style: { color: 'var(--green)' } }, '✓ ' + r.sent_to_email)
                      : React.createElement('span', { style: { color: 'var(--text-muted)' } }, 'טרם נשלח')
                  ),
                  React.createElement('td', { style: { padding: '10px 16px' } },
                    React.createElement('div', { style: { display: 'flex', gap: '6px' } },
                      r.public_url && React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { handleView(r); },
                        style: { padding: '4px 10px', fontSize: '11px' }
                      }, 'צפה'),
                      r.public_url && React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { copyReportLink(r); },
                        style: { padding: '4px 10px', fontSize: '11px' }
                      }, copiedReportId === r.id ? '✓ הועתק' : 'העתק קישור'),
                      r.has_pdf && React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { handleDownload(r.id); },
                        style: { padding: '4px 10px', fontSize: '11px' }
                      }, 'PDF'),
                      r.has_pdf && React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { openSendDialog(r.id, r.month); },
                        style: { padding: '4px 10px', fontSize: '11px' }
                      }, 'שלח'),
                      React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { handleDelete(r.id); },
                        style: { padding: '4px 10px', fontSize: '11px', color: 'var(--red)' }
                      }, 'מחק')
                    )
                  )
                );
              })
            )
          )
    )
  );
}